import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './pagination';

/** Tipos de área na hierarquia da empresa (PRD §8, RF-08): diretoria → gerência → departamento. */
export const DEPARTMENT_TYPES = ['DIRECTORATE', 'MANAGEMENT', 'DEPARTMENT'] as const;
export type DepartmentTypeName = (typeof DEPARTMENT_TYPES)[number];

export const DEPARTMENT_TYPE_LABELS: Record<DepartmentTypeName, string> = {
  DIRECTORATE: 'Diretoria',
  MANAGEMENT: 'Gerência',
  DEPARTMENT: 'Departamento',
};

/** Nível na hierarquia: a área de baixo tem nível maior que a de cima. */
export const DEPARTMENT_TYPE_LEVEL: Record<DepartmentTypeName, number> = {
  DIRECTORATE: 0,
  MANAGEMENT: 1,
  DEPARTMENT: 2,
};

/** Uma área pode ficar abaixo de outra só se for de nível inferior (ex.: gerência abaixo de diretoria). */
export function canBeChildOf(child: DepartmentTypeName, parent: DepartmentTypeName): boolean {
  return DEPARTMENT_TYPE_LEVEL[child] > DEPARTMENT_TYPE_LEVEL[parent];
}

export const departmentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  type: z.enum(DEPARTMENT_TYPES),
  parentId: z.string().uuid().nullable(),
  archivedAt: z.string().nullable(),
  /** Pessoas ligadas à área. */
  memberCount: z.number().int(),
});
export type Department = z.infer<typeof departmentSchema>;
export const departmentListSchema = z.object({ items: z.array(departmentSchema) });

export const departmentInputSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome da área.').max(120, 'Nome longo demais.'),
  type: z.enum(DEPARTMENT_TYPES),
  parentId: z.string().uuid('Área superior inválida.').nullish(),
});
export type DepartmentInput = z.input<typeof departmentInputSchema>;

export const businessUnitSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  code: z.string().nullable(),
  /** Peso na média ponderada da consolidação dos KPIs (T-067, DEC-037). */
  weight: z.number(),
  archivedAt: z.string().nullable(),
  memberCount: z.number().int(),
});
export type BusinessUnit = z.infer<typeof businessUnitSchema>;
export const businessUnitListSchema = paginatedSchema(businessUnitSchema);

export const businessUnitInputSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome da unidade.').max(120, 'Nome longo demais.'),
  code: z
    .string()
    .trim()
    .max(20, 'Código com no máximo 20 caracteres.')
    .transform((v) => v.toUpperCase())
    .refine(
      (v) => v === '' || /^[A-Z0-9][A-Z0-9._-]*$/.test(v),
      'Use letras, números, ponto, hífen ou sublinhado.',
    )
    .transform((v) => (v === '' ? null : v))
    .nullish(),
  /** Peso na média ponderada (padrão 1; ao editar sem informar, mantém o atual). */
  weight: z.coerce
    .number({ message: 'Informe o peso em números.' })
    .gt(0, 'O peso precisa ser maior que zero.')
    .max(99999, 'Peso alto demais.')
    .refine((v) => Math.round(v * 1e4) / 1e4 === v, 'Use no máximo 4 casas decimais.')
    .optional(),
});
export type BusinessUnitInput = z.input<typeof businessUnitInputSchema>;

export const businessUnitListQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(100, 'Busca longa demais.').optional(),
  archived: z.enum(['true', 'false']).optional(),
});
